import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import type { User } from "@shared/models/auth";

const AUTH_REQUEST_TIMEOUT_MS = 8_000;

export class AuthCheckUnavailableError extends Error {
  constructor(public readonly reason: "timeout" | "network" | "server", message: string) {
    super(message);
    this.name = "AuthCheckUnavailableError";
  }
}

// Incident sequence this prevents: a Persona PATCH overlaps a slow current-user
// refetch, the 8s client deadline fires, null reaches AppRouter, and the persona
// screen is replaced by login even though the server never invalidated the
// session. Only an explicit 401 may produce null; transport failures stay errors.
export async function fetchUser(): Promise<User | null> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), AUTH_REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch("/api/auth/user", {
      credentials: "include",
      signal: controller.signal,
    });

    if (response.status === 401) {
      return null;
    }

    if (!response.ok) {
      throw new AuthCheckUnavailableError(
        "server",
        `Authentication check is temporarily unavailable (${response.status}).`,
      );
    }

    return response.json();
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new AuthCheckUnavailableError("timeout", "Authentication check timed out.");
    }
    if (error instanceof AuthCheckUnavailableError) throw error;
    throw new AuthCheckUnavailableError("network", "Could not reach the authentication service.");
  } finally {
    window.clearTimeout(timeout);
  }
}

async function logout(): Promise<void> {
  await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
  window.location.href = "/";
}

export function useAuth() {
  const queryClient = useQueryClient();
  const lastConfirmedUser = useRef<User | null>(null);
  const query = useQuery<User | null>({
    queryKey: ["/api/auth/user"],
    queryFn: fetchUser,
    retry: false,
    staleTime: 1000 * 60 * 5,
  });
  const explicitAnonymous = query.isSuccess && query.data === null;
  if (query.data) lastConfirmedUser.current = query.data;
  if (explicitAnonymous) lastConfirmedUser.current = null;
  const user = query.data ?? (query.isError ? lastConfirmedUser.current : null);
  // React Query keeps a query with cached data in a successful data state when
  // a later refetch fails, so `isError` alone misses the exact reconnect case.
  const isReconnecting = query.error != null;

  // Attach the authenticated user's id to Sentry events once it resolves, and
  // clear it when the user becomes anonymous. Sentry is already initialized at
  // module load in main.tsx (before React mounts), so setUser is always safe to
  // call here. This also covers the post-login full-page-reload redirect, after
  // which the user is re-fetched and this effect runs again.
  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.id });
    } else if (explicitAnonymous) {
      Sentry.setUser(null);
    }
  }, [user, explicitAnonymous]);

  useEffect(() => {
    if (!query.error) return;
    const reason = query.error instanceof AuthCheckUnavailableError ? query.error.reason : "network";
    Sentry.captureMessage("auth.current_user_check_unavailable", {
      level: "warning",
      tags: { auth_check_reason: reason },
    });
  }, [query.error]);

  const logoutMutation = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      queryClient.setQueryData(["/api/auth/user"], null);
      Sentry.setUser(null);
    },
  });

  return {
    user,
    isLoading: query.isLoading,
    isReconnecting,
    authError: query.error,
    retryAuth: query.refetch,
    isAuthenticated: !!user,
    logout: logoutMutation.mutate,
    isLoggingOut: logoutMutation.isPending,
  };
}
